import { useProjectParams } from '@hooks/useProjectParams';
import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { FilesSelector } from 'components/FilesSelector/FilesSelector';
import { ButtonWithTooltip } from 'components/Form/ButtonWithTooltip';
import { ModalDialog } from 'components/ModalDialog';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm, TableSettings } from 'components/TableSettings';
import { PriceListTableCount } from 'components/TotalCount/PriceListTableCount';
import { UploadStatus } from 'components/UploadStatus';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type { ProjectData } from 'gql/graphql';
import { ContractStatusEnum, ProjectMethodologyEnum, ProjectStatusEnum, WorkOrderPriceStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { usePin } from 'hooks/usePin';
import { useProjectData } from 'hooks/useProjectData';
import { useSelection } from 'hooks/useSelection';
import { useContractConstructionTypes } from 'pages/Project/Common/Contract/hooks/useContractConstructionTypes';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { CreateOrEditUer } from 'pages/Project/UerProject/Uer/CreateOrEditUer/CreateOrEditUer';
import { Badge, Button, cnMixSpace, Grid, GridItem, IconWarning, Layout, useDebounce } from 'uikit';

import type { ColDef, ColGroupDef, GridReadyEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import cn from 'classnames';
import { noun } from 'plural-ru';
import type { CSSProperties } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';

import { useContractData } from '../../Contract/hooks/useContractData';
import { PriceListUerWorkScopesModal } from '../PriceListUerWorkScopesModal';

import type { RowData } from './types';
import { usePriceListUerColumns } from './uerColumns';
import { priceListUerRemoveUnusedRows, uerRowReadyInfo } from './uerQueries';
import { usePriceListUER } from './usePriceListUER';
import { useUploadPriceListUer } from './useUploadPriceListUER';

import Styles from './PriceListUER.module.css';

const RESULT_MODAL_TITLE = 'Загрузка Сборника расценок';

export const PriceListUER = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id: projectId, methodology } = useProjectParams();
    const { projectData, refreshProject, setProjectData } = useProjectData(projectId);
    const [position, setPosition] = useState<number | undefined>();

    const { isHeaderOpened } = useContext(PageHeaderLayoutContext);

    const [constructionTypesData, constructionTypesStatus] = useContractConstructionTypes(
        projectData?.contractId,
        ProjectMethodologyEnum.Uer,
    );

    const [contractData] = useContractData(projectData?.contractId);

    const catalogUerVersion = contractData?.catalogUerBase?.version;
    const catalogName = contractData?.catalogUerBase?.name || '';

    const [expandedRowId, setExpandedRowId] = useState<RowData['id'] | null>(null);

    const { priceList: priceListRules } = useAccess();

    const ref = useRef<AgGridReact<RowData>>(null);
    const tableSettingsRef = useRef<HTMLDivElement>(null);

    const isProjectStatusInImplementation = projectData?.status === ProjectStatusEnum.InImplementation;

    const getContractData = projectData?.contractNavigation?.data?.contractNavData;

    const isContractStatusWork = getContractData?.status === ContractStatusEnum.InImplementation;

    const handleExpandRow = useCallback((id: RowData['id']) => {
        setExpandedRowId(id);
    }, []);

    const handleCloseExpandedRowModal = useCallback(() => {
        setExpandedRowId(null);
    }, []);

    const { hasWorkKind, unreadyUerRowCount, isPriceCollectionLoaded, hasFilledMandatoryProperties } =
        projectData?.contractNavigation?.data?.contractNavData?.metadata ?? {};

    const { columns, filterValues, resetDataDependentFilters } = usePriceListUerColumns({
        constructionTypes: constructionTypesData,
        contractId: projectData?.contractId,
        contractStatus: getContractData?.status,
        handleExpandRow,
        unreadyUerRowCount,
        hasWorkKind,
        priceListRules,
    });

    const { datasource, totalCount, fetchedCount, refreshData } = usePriceListUER({
        contractId: projectData?.contractId,
        filterValues,
        gridRef: ref,
        constructionTypesData,
    });

    useEffect(() => {
        if (tableSettingsRef.current) {
            setPosition(tableSettingsRef.current?.getBoundingClientRect().bottom);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableSettingsRef.current?.getBoundingClientRect().bottom, isHeaderOpened]);

    useEffect(() => {
        if (typeof ref !== 'function' && ref?.current) {
            if (!totalCount) {
                ref?.current?.api?.showNoRowsOverlay();
            } else {
                ref?.current?.api?.hideOverlay();
            }
        }
    }, [totalCount, ref]);

    const [isUploadModalOpened, setIsUploadModalOpened] = useState(false);
    const [isUploadResultModalActive, setIsUploadResultModalActive] = useState(false);

    const [selection, , , setSelection] = useSelection('PriceListUERTable');
    const [pinnedItems, , , setPinned] = usePin('PriceListUERTable');

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const contractIdVariable = useMemo(() => {
        if (projectData?.contractId) {
            return {
                contractId: projectData?.contractId,
            };
        }

        return null;
    }, [projectData?.contractId]);

    const [, removeUnusedUerStatus, , removeUnusedUerRowsRequest] = useRequest({
        requestId: 'priceListFactorsRemoveUnusedRows',
        query: priceListUerRemoveUnusedRows,
        withEffect: false,
    });

    const memoizedRequestIds = useMemo(
        () => [
            'uerPriceListUerSectionNameFilter',
            'uerPriceListUerGroupFullNameFilter',
            'uerPriceListUerCodeFilter',
            'uerPriceListUerNameFilter',
            'uerPriceListUerChapterFilter',
            'uerPriceListUerTypeFilter',
            'uerPriceListUerJustificationFilter',
            'uerPriceListUerStatusFilter',
        ],
        [],
    );

    const [resetPageData] = useResetMultipleStoreData({
        requestIds: memoizedRequestIds,
    });

    useEffect(
        () => () => {
            resetPageData();
        },
        [resetPageData],
    );

    const {
        isSubmitted,
        uploadPriceListUerData,
        uploadPriceListUerStatus,
        uploadPriceListUerError,
        makeRequestHandler,
        handleCloseModalErrorUploadPriceList,
        handleCreateReportUploadPriceList,
        priceListCollectionReset,
    } = useUploadPriceListUer(projectData?.contractId);

    const [uerRowReadyInfoData, uerRowReadyInfoStatus, , uerRowReadyInfoMakeRequest, uerRowReadyInfoSetData] =
        useRequest({
            requestId: 'uerRowReadyInfo',
            query: uerRowReadyInfo,
            withEffect: false,
        });

    const fullSetColumns = useMemo<(ColDef | ColGroupDef)[]>(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean; isPinDefDisabled: boolean };

                    if (idx < 6) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const breadCrumbsMtrItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectData?.uniqueId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [methodology, projectData?.uniqueId, projectData?.name],
    );

    const updateUer = useDebounce(() => {
        refreshData();
        resetDataDependentFilters();
    }, 500);

    const updateUerHandler = useCallback(() => {
        if (contractIdVariable) {
            dispatch(uerRowReadyInfoMakeRequest(contractIdVariable));
            updateUer();
        }
    }, [contractIdVariable, dispatch, uerRowReadyInfoMakeRequest, updateUer]);

    useEffect(() => {
        if (uploadPriceListUerStatus !== RequestStatus.FETCHING) {
            setIsUploadResultModalActive(true);
        }
    }, [uploadPriceListUerStatus]);

    useEffect(() => {
        if (removeUnusedUerStatus === RequestStatus.SUCCESS) {
            refreshData();
        }
    }, [dispatch, refreshData, removeUnusedUerStatus]);

    const handleModalOpen = useCallback(() => {
        setIsUploadModalOpened((prev) => !prev);
    }, []);

    const handleCloseResultModal = useCallback(() => {
        setIsUploadResultModalActive(false);
        setIsUploadModalOpened(false);
        priceListCollectionReset(undefined);
    }, [priceListCollectionReset]);

    const handleReloadPriceList = useCallback(() => {
        refreshData();
        resetDataDependentFilters();
        refreshProject();

        handleModalOpen();
        handleCloseResultModal();
    }, [handleCloseResultModal, handleModalOpen, refreshData, refreshProject, resetDataDependentFilters]);

    const isUnableToLoadPredicate = useCallback((status) => status === WorkOrderPriceStatusEnum.ContractCondition, []);

    const isBtnDisabled = removeUnusedUerStatus === RequestStatus.FETCHING;

    useEffect(() => {
        if (uerRowReadyInfoStatus === RequestStatus.SUCCESS && uerRowReadyInfoData?.uerUerRowReadyInfo?.data) {
            const newMetaData = {
                unreadyUerRowCount: uerRowReadyInfoData?.uerUerRowReadyInfo.data.unreadyUerRowCount,
                isPriceCollectionLoaded: uerRowReadyInfoData?.uerUerRowReadyInfo.data?.isPriceCollectionLoaded,
            };

            setProjectData({
                ...projectData,
                contractNavigation: {
                    ...projectData?.contractNavigation,
                    data: {
                        ...projectData?.contractNavigation?.data,
                        contractNavData: {
                            ...projectData?.contractNavigation?.data?.contractNavData,
                            metadata: {
                                ...projectData?.contractNavigation?.data?.contractNavData?.metadata,
                                ...newMetaData,
                            },
                        },
                    },
                },
            } as ProjectData);
            dispatch(uerRowReadyInfoSetData(undefined));
        }
    }, [
        dispatch,
        hasFilledMandatoryProperties,
        projectData,
        setProjectData,
        uerRowReadyInfoData?.uerUerRowReadyInfo.data,
        uerRowReadyInfoSetData,
        uerRowReadyInfoStatus,
    ]);

    const contractUnreadyUerMsg = `${unreadyUerRowCount} ${noun(
        unreadyUerRowCount ?? 0,
        ...['расценка', 'расценки', 'расценок'],
    )} без главы в расчете и типа уер`;

    const onFullSetTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setPinned,
                setSelection,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionQuery: removeUnusedUerRowsRequest,
            variables: contractIdVariable,
        });

    const onGridReady = useCallback((event: GridReadyEvent) => {
        event.api.showNoRowsOverlay();
    }, []);

    const isHasWorkOrders = getContractData?.hasWorkOrders;

    return (
        <ProjectPageLayout
            title="Прейскурант расценок"
            breadCrumbsItems={breadCrumbsMtrItems}
            headerRightSide={
                isPriceCollectionLoaded && unreadyUerRowCount ? (
                    <Badge icon={IconWarning} status="error" label={contractUnreadyUerMsg} style={{ marginLeft: 12 }} />
                ) : undefined
            }
            isHeaderCollapsable
            headerBottom={
                <div className="wrapperToolbarLine">
                    <Layout>
                        <ButtonWithTooltip
                            label="Загрузить Сборник расценок"
                            view="secondary"
                            size="s"
                            onClick={handleModalOpen}
                            className={cnMixSpace({ mR: 's' })}
                            disabled={
                                isBtnDisabled ||
                                !isContractStatusWork ||
                                !isProjectStatusInImplementation ||
                                isHasWorkOrders ||
                                !priceListRules?.canCreate
                            }
                            tooltipText="Сборник расценок не может быть загружен, так как в договоре уже имеются наряд-заказы"
                            direction="upCenter"
                            style={{ display: 'inline-block' }}
                            isTooltipHidden={!isHasWorkOrders}
                        />
                        <Button
                            onClick={handleOpenConfirmationDialog}
                            label="Удалить неиспользуемые записи"
                            view="secondary"
                            size="s"
                            disabled={
                                isBtnDisabled ||
                                !isContractStatusWork ||
                                !isProjectStatusInImplementation ||
                                !priceListRules?.canDelete
                            }
                        />
                    </Layout>
                </div>
            }
        >
            <Routes>
                <Route
                    path="/editUer/:uerId"
                    element={
                        <CreateOrEditUer
                            onSave={updateUerHandler}
                            closeModal={() => navigate('./')}
                            noShowButton
                            isUnableToLoadPredicate={isUnableToLoadPredicate}
                            resetPageData={resetPageData}
                            contractId={projectData?.contractId}
                            catalogVersion={catalogUerVersion}
                            catalogName={catalogName}
                            isReadOnly={!priceListRules?.canEdit}
                        />
                    }
                />
            </Routes>

            <Layout direction="column">
                <>
                    <ModalDialog
                        title="Загрузка Сборника расценок"
                        isOpen={isUploadModalOpened}
                        hasOverlay
                        onClickOutside={handleModalOpen}
                        onEsc={handleModalOpen}
                        onClose={handleModalOpen}
                        withBottomButtons={false}
                        withTitleCloseButton
                    >
                        <div className={Styles.wrapperFilesSelector}>
                            <FilesSelector
                                accept={['.risx']}
                                isLoading={uploadPriceListUerStatus === RequestStatus.FETCHING}
                                typeName=".risx"
                                manualOperationName="выберите файл вручную"
                                onFilesChange={makeRequestHandler}
                            />
                        </div>
                    </ModalDialog>
                    {projectData?.contractId && constructionTypesStatus === RequestStatus.SUCCESS && (
                        <>
                            <Grid cols={2} ref={tableSettingsRef}>
                                <GridItem>
                                    <Grid cols={2} yAlign="center">
                                        <GridItem style={{ display: 'flex' }}>
                                            <PriceListTableCount totalCount={fetchedCount} totalItems={totalCount} />
                                        </GridItem>
                                    </Grid>
                                </GridItem>
                                <GridItem style={{ display: 'flex', justifyContent: 'right' }}>
                                    <TableSettings
                                        columns={fullSetSettingsColumns as any}
                                        onConfirm={onFullSetTableSettingsConfirm}
                                        isColumnVisible={isColumnVisible}
                                        showPinColumn
                                        tableApi={ref?.current}
                                    />
                                </GridItem>
                            </Grid>
                            <div
                                className={cn('ag-theme-alpine', Styles.priceListUER)}
                                style={
                                    {
                                        '--tableStartPosition': `${position ?? 0}px`,
                                    } as CSSProperties
                                }
                            >
                                <AgGridTable
                                    datasource={datasource}
                                    columnDefs={fullSetColumns}
                                    rowModelType="infinite"
                                    maxConcurrentDatasourceRequests={1}
                                    maxBlocksInCache={50}
                                    cacheBlockSize={50}
                                    onGridReady={onGridReady}
                                    ref={ref}
                                    suppressCellFocus
                                    context={{
                                        tableUniqueId: 'uerList',
                                    }}
                                />
                            </div>
                        </>
                    )}
                </>
            </Layout>

            {expandedRowId && <PriceListUerWorkScopesModal id={expandedRowId} onClose={handleCloseExpandedRowModal} />}

            {uploadPriceListUerStatus === RequestStatus.SUCCESS &&
                uploadPriceListUerData?.uerImportPriceListCollection?.result === true && (
                    <UploadStatus
                        isOpened={isUploadResultModalActive}
                        handleCloseModal={handleCloseResultModal}
                        statusText="К расценкам успешно добавлены Глава в расчете и Тип УЕР"
                        titleText={RESULT_MODAL_TITLE}
                        status="success"
                    >
                        <Button label="Перейти в Прейскурант" onClick={handleReloadPriceList} />
                    </UploadStatus>
                )}
            <ErrorHandlingModal
                titleText={RESULT_MODAL_TITLE}
                isSubmitted={isSubmitted}
                statusRequest={uploadPriceListUerStatus}
                dataRequest={uploadPriceListUerData?.uerImportPriceListCollection}
                errorsRequest={uploadPriceListUerError}
                handleCloseModalError={handleCloseModalErrorUploadPriceList}
                handleCreateReport={handleCreateReportUploadPriceList}
            />
            <ConfirmationDialog
                textHeader="Удаление неиспользуемых записей"
                textConfirmation="Вы уверены, что хотите удалить записи Прейскуранта в статусах «Черновик» и «Новая запись», неиспользуемые в Наряд-заказах и Актах КС-2?"
                confirmButtonText="Удалить"
                cancelButtonText="Не удалять"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ProjectPageLayout>
    );
};

PriceListUER.displayName = 'PriceListUER';
